import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote, ExternalLink } from "lucide-react";
import { trackEvent } from "@/hooks/useBusinessData";

export default function ReviewCarousel({ reviews, ratingMeta }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef(null);

  useEffect(() => {
    if (paused || reviews.length === 0) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % reviews.length);
    }, 6500);
    return () => clearInterval(t);
  }, [paused, reviews.length]);

  const go = (dir) => {
    setIdx((i) => (i + dir + reviews.length) % reviews.length);
  };

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) go(diff > 0 ? 1 : -1);
    touchStart.current = null;
  };

  if (!reviews || reviews.length === 0) return null;
  const current = reviews[idx];
  const platform = ratingMeta.platform || "Yelp";
  const yelpUrl =
    ratingMeta.url || "https://m.yelp.com/biz/shecuts-barber-studio-atlanta";

  return (
    <section
      id="reviews"
      className="relative bg-black text-white py-24 md:py-40 border-t border-white/10 overflow-hidden"
      data-testid="reviews-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-6">
            <div className="eyebrow mb-6">§ What Clients Say</div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
            >
              A decade of
              <br />
              <span className="italic text-zinc-400">five-star loyalty.</span>
            </motion.h2>
            <p className="mt-6 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              Real experiences from clients who have trusted Yolanda and SheCuts over the years.
            </p>
          </div>
          <div className="col-span-12 md:col-span-6 md:pl-8 flex flex-col justify-end">
            <div className="flex flex-wrap items-end gap-x-10 gap-y-6">
              <div>
                <div className="font-serif text-7xl md:text-9xl leading-none">
                  {ratingMeta.rating ? ratingMeta.rating.toFixed(1) : "5.0"}
                </div>
                <div className="flex mt-2">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 md:w-5 md:h-5 fill-current"
                      style={{ color: "var(--sc-star)" }}
                    />
                  ))}
                </div>
              </div>
              <div className="border-l border-white/15 pl-6 md:pl-10">
                <div className="eyebrow text-zinc-400">Verified {platform} Reviews</div>
                <div className="font-serif text-2xl md:text-3xl mt-1">
                  A decade of trusted clients
                </div>
                <a
                  href={yelpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent("yelp_reviews_clicked", { source: "reviews_header" })
                  }
                  data-testid="reviews-yelp-link"
                  className="mt-4 inline-flex items-center gap-2 eyebrow text-white line-hover"
                >
                  View all on {platform}
                  <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div
          className="relative"
          data-testid="reviews-carousel"
          aria-live="polite"
        >
          <AnimatePresence mode="wait">
            <motion.article
              key={current.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
              className="grid grid-cols-12 gap-6 md:gap-10 items-start border-t border-white/10 pt-10 md:pt-14 min-h-[300px] md:min-h-[380px]"
              data-testid={`review-${current.id}`}
            >
              <div className="col-span-12 md:col-span-2">
                <Quote
                  className="w-10 h-10 md:w-14 md:h-14 text-white/40"
                  strokeWidth={0.8}
                />
              </div>
              <div className="col-span-12 md:col-span-8">
                <p className="font-serif text-xl md:text-3xl leading-snug text-white">
                  “{current.text || current.review_text}”
                </p>
                <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-4 md:gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center font-serif text-lg">
                      {(current.reviewer_name || current.name)
                        .replace(/[^A-Za-z]/g, "")
                        .charAt(0)}
                    </div>
                    <div>
                      <div className="font-serif text-lg leading-tight">
                        {current.reviewer_name || current.name}
                      </div>
                      <div className="eyebrow text-zinc-500 mt-1">
                        {current.source_label || "Yelp"}
                      </div>
                    </div>
                  </div>
                  <a
                    href={current.source_url || yelpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent("yelp_reviews_clicked", {
                        source: "review_card",
                        review_id: current.id,
                      })
                    }
                    data-testid={`review-yelp-link-${current.id}`}
                    className="inline-flex items-center gap-2 eyebrow text-white line-hover md:ml-auto"
                  >
                    View on Yelp
                    <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                  </a>
                </div>
              </div>
              <div className="col-span-12 md:col-span-2 flex md:justify-end">
                <div className="flex">
                  {[...Array(current.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-current"
                      style={{ color: "var(--sc-star)" }}
                    />
                  ))}
                </div>
              </div>
            </motion.article>
          </AnimatePresence>

          <div className="mt-10 md:mt-14 flex items-center justify-between border-t border-white/10 pt-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => go(-1)}
                data-testid="reviews-prev"
                aria-label="Previous review"
                className="p-3 rounded-full border border-white/25 hover:bg-white hover:text-black transition-colors"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={1.4} />
              </button>
              <button
                onClick={() => go(1)}
                data-testid="reviews-next"
                aria-label="Next review"
                className="p-3 rounded-full border border-white/25 hover:bg-white hover:text-black transition-colors"
              >
                <ChevronRight className="w-4 h-4" strokeWidth={1.4} />
              </button>
            </div>
            <div className="font-mono text-xs text-zinc-500">
              {String(idx + 1).padStart(2, "0")} —{" "}
              {String(reviews.length).padStart(2, "0")}
            </div>
            <div className="hidden md:flex items-center gap-1.5">
              {reviews.map((r, i) => (
                <button
                  key={r.id}
                  onClick={() => setIdx(i)}
                  aria-label={`Review ${i + 1}`}
                  className={`h-1 transition-all rounded-full ${
                    i === idx ? "w-8 bg-white" : "w-3 bg-white/25"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
